/* eslint-disable @typescript-eslint/no-misused-promises */
import { Fragment, useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  approveVideoSchema,
  ApproveVideoSchema,
  uploadVideoSchema,
  UploadVideoSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import FilePond from "@/components/ui/file-pond";
import { Input } from "@/components/ui/input";

import {
  useCandidateProfileApproveVideosMutation,
  useCandidateProfileUploadVideoMutation,
} from "./mutations";
import { ProfileVideo } from "@/redux/features/candidateProfileSlice";

interface Props {
  userId: string;
  videos?: ProfileVideo[];
  open: boolean;
  profileMode?: boolean;
  setOpen: (v: boolean) => void;
  onSuccess?: (data: ProfileVideo[]) => void;
}

const CandidateProfileVideoDialog = ({
  userId,
  videos,
  open,
  setOpen,
  profileMode,
  onSuccess: successCallback,
}: Props) => {
  const [mode, setMode] = useState<"update" | "upload" | "skip" | undefined>();

  const {
    mutate: approveVideosMutation,
    isPending: approveVideosMutationLoading,
    isError: approveVideosMutationError,
  } = useCandidateProfileApproveVideosMutation(userId, successCallback);

  const {
    mutate: uploadVideoMutation,
    isPending: uploadVideoMutationLoading,
    isError: uploadVideoMutationError,
  } = useCandidateProfileUploadVideoMutation(userId, successCallback);

  const approvalForm = useForm<ApproveVideoSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(approveVideoSchema)(data, context, opts);
    },
    defaultValues: {
      videos:
        videos?.map((video) => ({
          id: video.id,
          title: video.title ?? "",
          summary: video.summary ?? "",
          showOnProfile: video.showOnProfile ?? false,
        })) ?? [],
    },
  });

  const uploadForm = useForm<UploadVideoSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(uploadVideoSchema)(data, context, opts);
    },
    defaultValues: {
      video: undefined,
      title: "",
      summary: "",
    },
  });

  function onSubmit() {
    if (mode === "update") {
      void approvalForm.handleSubmit(onSubmitApprovalForm)();
    } else if (mode === "upload") {
      void uploadForm.handleSubmit(onSubmitUploadVideo)();
    } else {
      successCallback?.(videos ?? []);
    }
  }

  function onSubmitApprovalForm(data: ApproveVideoSchema) {
    if (approveVideosMutationLoading) return;
    approveVideosMutation(data);
  }

  function onSubmitUploadVideo(data: UploadVideoSchema) {
    if (uploadVideoMutationLoading) return;
    uploadVideoMutation(data);
  }

  useEffect(() => {
    approvalForm.reset({
      videos:
        videos?.map((video) => ({
          id: video.id,
          title: video.title ?? "",
          summary: video.summary ?? "",
          showOnProfile: video.showOnProfile ?? false,
        })) ?? [],
    });

    uploadForm.reset({
      video: undefined,
      title: "",
      summary: "",
    });

    if (mode === "update" && !videos?.length) {
      setMode(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, mode]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-0"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader className="p-6">
          <div className="flex items-center gap-2">
            <DialogTitle>
              {profileMode
                ? "Update Your Video(s)"
                : "Add Your Video Interview"}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Review your videos and add them to your profile
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
          <RadioGroup
            value={mode}
            onValueChange={(data) =>
              setMode(data as "update" | "upload" | "skip")
            }
          >
            {Boolean(videos?.length) && (
              <div
                className={cn(
                  "p-4 flex flex-col gap-4 border rounded-xl border-[#e9eaeb]",
                  mode === "update" && "bg-[#f9f9fb]",
                )}
              >
                <div className="flex flex-row gap-2 items-center">
                  <RadioGroupItem value="update" />
                  <label
                    className={cn(
                      "text-sm font-medium text-[#0c0d0d]",
                      mode === "update" && "font-semibold",
                    )}
                  >
                    {profileMode
                      ? "Change displayed videos"
                      : "Use my candidate interview"}
                  </label>
                </div>
                {mode === "update" && (
                  <EditVisibilityForm
                    form={approvalForm}
                    videos={videos}
                    mutationError={approveVideosMutationError}
                    editMode={profileMode}
                  />
                )}
              </div>
            )}
            <div
              className={cn(
                "p-4 flex flex-col gap-4 border rounded-xl border-[#e9eaeb]",
                mode === "upload" && "bg-[#f9f9fb]",
              )}
            >
              <div className="flex flex-row gap-2 items-center">
                <RadioGroupItem value="upload" />
                <label
                  className={cn(
                    "text-sm font-medium text-[#0c0d0d]",
                    mode === "upload" && "font-semibold",
                  )}
                >
                  {profileMode ? "Upload video" : "Upload my own intro video"}
                </label>
              </div>
              {mode === "upload" && (
                <UploadForm
                  form={uploadForm}
                  mutationError={uploadVideoMutationError}
                />
              )}
            </div>
            {!profileMode && (
              <div
                className={cn(
                  "p-4 flex flex-col gap-4 border rounded-xl border-[#e9eaeb]",
                  mode === "skip" && "bg-[#f9f9fb]",
                )}
              >
                <div className="flex flex-row gap-2 items-center">
                  <RadioGroupItem value="skip" />
                  <label
                    className={cn(
                      "text-sm font-medium text-[#0c0d0d]",
                      mode === "skip" && "font-semibold",
                    )}
                  >
                    Skip for now
                  </label>
                </div>
              </div>
            )}
          </RadioGroup>
          <div className="flex justify-between">
            <Button
              type="button"
              className="mt-2"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="mt-2"
              variant="secondary"
              onClick={onSubmit}
            >
              Continue
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const EditVisibilityForm = ({
  form,
  videos,
  mutationError,
  editMode,
}: {
  form: UseFormReturn<ApproveVideoSchema>;
  videos?: ProfileVideo[];
  mutationError: boolean;
  editMode?: boolean;
}) => {
  const [selectedVideo, setSelectedVideo] = useState<string | undefined>(
    undefined,
  );

  return (
    <>
      <Form {...form}>
        <form className="space-y-4 w-full">
          {mutationError && (
            <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
              Something went wrong when approving the videos, please try again
              later.
            </p>
          )}
          {videos?.map((_video, idx) => {
            return (
              <Fragment key={`videos.${idx}.showOnProfile`}>
                <FormField
                  control={form.control}
                  name={`videos.${idx}.showOnProfile`}
                  render={({ field }) => (
                    <FormItem>
                      <div
                        className={cn(
                          "p-3 flex flex-row items-start gap-3 self-stretch rounded-md border",
                          field.value
                            ? "border-[#0b6e4f] bg-[#e6f3ef]"
                            : "border-[#e5e7eb]",
                        )}
                      >
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(state) => {
                              field.onChange(
                                typeof state === "string" ? false : state,
                              );
                            }}
                          />
                        </FormControl>
                        <div
                          className="max-h-12 max-w-20 relative flex-1 cursor-pointer"
                          onClick={() =>
                            setSelectedVideo(videos[idx]?.videoLink)
                          }
                        >
                          <video
                            className="max-h-12 max-w-20 w-full h-auto object-cover"
                            src={videos[idx]?.videoLink}
                            controls={false}
                            muted
                            preload="metadata"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="bg-white/80 rounded-full p-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-black"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M6.5 5.5v9l7-4.5-7-4.5z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 self-stretch">
                          {editMode && !videos[idx]?.hireflixVideo ? (
                            <>
                              <FormField
                                control={form.control}
                                name={`videos.${idx}.title`}
                                key={`videos.${idx}.title`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel required>
                                      Title (30 characters or less)
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="My background"
                                        maxLength={30}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`videos.${idx}.summary`}
                                key={`videos.${idx}.summary`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel required>
                                      Summary (100 characters or less)
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="A brief runthrough of my work history"
                                        maxLength={100}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </>
                          ) : (
                            <>
                              <p className="text-base font-semibold text-black">
                                {videos[idx]?.title}
                              </p>
                              <p className="line-clamp-2 hover:line-clamp-none overflow-hidden text-sm text-[#727E94] overflow-ellipsis">
                                {videos[idx]?.summary}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Fragment>
            );
          })}
        </form>
      </Form>
      <VideoPlayer
        videoLink={selectedVideo}
        closeFn={() => setSelectedVideo(undefined)}
      />
    </>
  );
};

const UploadForm = ({
  form,
  mutationError,
}: {
  form: UseFormReturn<UploadVideoSchema>;
  mutationError: boolean;
}) => {
  return (
    <>
      <Form {...form}>
        <form className="space-y-4 w-full">
          {mutationError && (
            <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
              Something went wrong when uploading the video, please try again
              later.
            </p>
          )}
          <FormField
            control={form.control}
            name="video"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Video</FormLabel>
                <FormControl>
                  <FilePond
                    description="Please upload a short video, no more than 100MB."
                    value={field.value ? [field.value] : []}
                    accept={"video/*"}
                    maxFileSizeKiloBytes={102400}
                    onChange={(files) => {
                      field.onChange(files[0]);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Title (30 characters or less)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="My background"
                    maxLength={30}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Summary (100 characters or less)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="A brief runthrough of my work history"
                    maxLength={100}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </>
  );
};

const VideoPlayer = ({
  videoLink,
  closeFn,
}: {
  videoLink?: string;
  closeFn: () => void;
}) => {
  return (
    <Dialog
      open={Boolean(videoLink)}
      onOpenChange={(open) => !open && closeFn()}
    >
      <DialogContent noClose>
        <DialogHeader className="sr-only">
          <DialogTitle>Video Playback</DialogTitle>
          <DialogDescription>Watch the uploaded video</DialogDescription>
        </DialogHeader>
        <video
          className="w-full h-auto object-cover"
          src={videoLink}
          controls
          autoPlay
          preload="metadata"
        />
      </DialogContent>
    </Dialog>
  );
};

export default CandidateProfileVideoDialog;
